<template>
  <f7-card-content hide-on-scroll>
    <swiper-container
      orientation="horizontal"
      :pagination="true"
      :space-between="true"
      :slides-per-view="1"
      :centered-slides="true"
      class="transactions_swiper"
    >
      <swiper-slide v-if="barChartSeries.length > 0" id="barChartSwiper">
        <apexchart
          type="bar"
          :options="barChartOptions"
          :series="barChartSeries"
        >
        </apexchart>
      </swiper-slide>
      <swiper-slide v-if="areaChartSeries.length > 0" id="areaChartSwiper">
        <apexchart
          type="area"
          :options="areaChartOptions"
          :series="areaChartSeries"
        >
        </apexchart>
      </swiper-slide>
      <swiper-slide v-if="pieChartSeries.length > 0" id="pieChartSwiper">
        <apexchart
          width="500"
          min-height="200"
          type="donut"
          :options="pieChartOptions"
          :series="pieChartSeries"
        >
        </apexchart>
      </swiper-slide>
    </swiper-container>
  </f7-card-content>
</template>
<style scoped></style>
<script setup>
import { ref, onMounted } from "vue";
import { f7 } from "framework7-vue";
import i18n from "../../js/i18n.js";
import { useCharts } from "../../js/composables/useCharts.js";
import { useKeycloakStore } from "../../js/pinia-stores/keycloak.js";
import { useAccountStore } from "../../js/pinia-stores/account.js";
import $dayjs from "../../js/day.js";

const props = defineProps({
  f7route: Object,
  f7router: Object,
});

const dataValue = ref([]);
const axisData = ref([]);
const area_name = ref("");
const bar_name = ref("");
const pieData = ref([]);
const pieLabel = ref([]);
const barData = ref([]);
const barLabel = ref([]);
const locales = ref([]);
const barChartOptions = ref({});
const pieChartOptions = ref({});
const areaChartOptions = ref({});
const areaChartSeries = ref([]);
const pieChartSeries = ref([]);
const barChartSeries = ref([]);
const colors = ref([]);
const groupingCriteria = ref([
  { value: "transactionYearQuarter", name: i18n.global.t("common.quarter") },
  { value: "transactionYearMonth", name: i18n.global.t("common.paymentDate") },
  { value: "transactionYearMonthWeek", name: i18n.global.t("common.paymentWeek") },
  { value: "transactionYearMonthDay", name: i18n.global.t("common.paymentDay") },
  { value: "senderName", name: i18n.global.t("common.sender") },
  { value: "status", name: i18n.global.t("common.status") },
  { value: "currency", name: i18n.global.t("common.currency") },
]);
const orderBy = ref("creation_date");
const orderingDir = ref("desc");
const isDesc = ref(true);
const groupBy = ref("transactionYearMonthDay");
const init = ref(true);

const {
  transactionList,
  apiWallet,
  structuredTransactionList,
  profile,
  isTablet,
  locale,
  userAccount,
  transactionStatus,
  currentTransactionList,
  setTransactionList: doSetTransactionList,
} = useCharts({
  dataValue,
  axisData,
  area_name,
  barData,
  barLabel,
  bar_name,
  pieData,
  pieLabel,
  barChartSeries,
  areaChartSeries,
  pieChartSeries,
  barChartOptions,
  areaChartOptions,
  pieChartOptions,
  groupingCriteria,
  orderBy,
  orderingDir,
  isDesc,
  groupBy,
});

function setTransactionList(value, init_status) {
  doSetTransactionList(value, init_status);
}

function initiateCharts() {
  areaChartOptions.value = {
    chart: {
      type: "area",
      locales: locales.value,
      defaultLocale: locale.value,
      id: "areaChart",
    },
    dataLabels: { enabled: false },
    tooltip: { fixed: { enabled: false, position: "topRight" } },
    title: { text: undefined, align: "bottom" },
    subtitle: { text: undefined, align: "bottom" },
    theme: {
      mode: "light",
      palette: "palette1",
      monochrome: { enabled: false, color: "#255aee", shadeTo: "light", shadeIntensity: 0.65 },
    },
    animations: {
      enabled: true,
      easing: "easeinout",
      speed: 800,
      animateGradually: { enabled: true, delay: 150 },
      dynamicAnimation: { enabled: true, speed: 350 },
    },
    toolbar: {
      show: true,
      tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true },
      export: {
        csv: { filename: `${apiWallet.value.tag} areaChart Transactions.csv`, columnDelimiter: ";" },
        svg: { filename: `${apiWallet.value.tag} areaChart Transactions.svg` },
        png: { filename: `${apiWallet.value.tag} areaChart Transactions.png` },
      },
    },
    stroke: { curve: "smooth" },
    xaxis: {
      categories: structuredTransactionList.value
        ? structuredTransactionList.value.find((item) => item.type === "area").categories
        : [],
    },
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.9, stops: [0, 90, 100] } },
    noData: { text: f7.preloader.show("multi") },
  };
  areaChartSeries.value = [
    {
      name: structuredTransactionList.value
        ? structuredTransactionList.value.find((item) => item.type === "area").name
        : [],
      data: structuredTransactionList.value
        ? structuredTransactionList.value.find((item) => item.type === "area").data
        : [],
    },
  ];

  pieChartOptions.value = {
    chart: { width: 500, type: "donut", defaultLocale: locale.value, locales: locales.value, id: "pieChart" },
    title: { text: undefined, align: "bottom" },
    subtitle: { text: undefined, align: "bottom" },
    legend: { show: isTablet.value ? true : false },
    theme: { mode: "light", palette: "palette1", monochrome: { enabled: false, color: "#255aee", shadeTo: "light", shadeIntensity: 0.65 } },
    animations: { enabled: true, easing: "easeinout", speed: 800, animateGradually: { enabled: true, delay: 150 }, dynamicAnimation: { enabled: true, speed: 350 } },
    toolbar: {
      show: true,
      tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true },
      export: {
        csv: { filename: `${apiWallet.value.tag} pieChart Transactions.csv`, columnDelimiter: ";" },
        svg: { filename: `${apiWallet.value.tag}pieChart Transactions.svg` },
        png: { filename: `${apiWallet.value.tag} pieChart Transactions.png` },
      },
    },
    colors: colors.value,
    labels: structuredTransactionList.value
      ? structuredTransactionList.value.find((item) => item.type === "pie").labels
      : [],
    responsive: [{ breakpoint: 480, options: { chart: { width: 200 }, legend: { position: "bottom" } } }],
  };
  pieChartSeries.value = structuredTransactionList.value
    ? structuredTransactionList.value.find((item) => item.type === "pie").series
    : [];

  barChartOptions.value = {
    chart: { type: "bar", height: 350, defaultLocale: locale.value, locales: locales.value, id: "barChart" },
    title: { text: undefined, align: "bottom" },
    subtitle: { text: undefined, align: "bottom" },
    theme: { mode: "light", palette: "palette1", monochrome: { enabled: false, color: "#255aee", shadeTo: "light", shadeIntensity: 0.65 } },
    animations: { enabled: true, easing: "easeinout", speed: 800, animateGradually: { enabled: true, delay: 150 }, dynamicAnimation: { enabled: true, speed: 350 } },
    toolbar: {
      show: true,
      tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true },
      export: {
        csv: { filename: `${apiWallet.value.tag} barChart Transactions.csv`, columnDelimiter: ";" },
        svg: { filename: `${apiWallet.value.tag} barChart Transactions.svg` },
        png: { filename: `${apiWallet.value.tag} barChart Transactions.png` },
      },
    },
    colors: colors.value,
    plotOptions: { bar: { columnWidth: "45%", distributed: true } },
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: {
      categories: structuredTransactionList.value
        ? structuredTransactionList.value.find((item) => item.type === "bar").categories
        : [],
    },
    labels: { style: { colors: colors.value, fontSize: "12px" } },
  };

  barChartSeries.value = [
    {
      data: structuredTransactionList.value
        ? structuredTransactionList.value.find((item) => item.type === "bar").data
        : [],
    },
  ];

  f7.preloader.hide();
}

onMounted(() => {
  colors.value = [
    "#008FFB","#00E396","#FEB019","#FF4560","#775DD0","#3F51B5","#03A9F4","#4CAF50","#F9CE1D","#FF9800",
    "#33B2DF","#546E7A","#D4526E","#13D8AA","#A5978B","#4ECDC4","#C7F464","#81D4FA","#546E7A","#FD6A6A",
    "#2B908F","#F9A3A4","#90EE7E","#FA4443","#69D2E7","#449DD1","#F86624","#EA3546","#662E9B","#C5D86D",
    "#D7263D","#1B998B","#2E294E","#F46036","#E2C044","#662E9B","#F86624","#F9C80E","#EA3546","#43BCCD",
    "#5C4742","#A5978B","#8D5B4C","#5A2A27","#C4BBAF","#A300D6","#7D02EB","#5653FE","#2983FF","#00B1F2",
  ];
  locales.value = [
    {
      name: locale.value,
      options: {
        months: $dayjs.months(),
        shortMonths: $dayjs.monthsShort(),
        days: $dayjs.weekdays(),
        shortDays: $dayjs.weekdays(),
        toolbar: {
          exportToSVG: i18n.global.t("common.downloadSVG"),
          exportToPNG: i18n.global.t("common.downloadPNG"),
          exportToSCV: i18n.global.t("common.downloadCSV"),
          menu: i18n.global.t("common.menu"),
          selection: i18n.global.t("common.selection"),
          selectionZoom: i18n.global.t("common.selectionZoom"),
          zoomIn: i18n.global.t("common.zoomIn"),
          zoomOut: i18n.global.t("common.zoomOut"),
          pan: i18n.global.t("common.panning"),
          reset: i18n.global.t("common.resetZoom"),
        },
      },
    },
  ];
  if (userAccount.value === null && useKeycloakStore().authenticated) {
    useAccountStore().loadAccount(profile.value.sub);
  }
  initiateCharts();

  const addExternalClassToMenuItems = () => {
    const menuItems = document.querySelectorAll(".apexcharts-menu-item");
    menuItems.forEach((item) => {
      if (!item.classList.contains("external")) {
        item.classList.add("external");
      }
    });
  };
  addExternalClassToMenuItems();
  const observer = new MutationObserver(() => {
    addExternalClassToMenuItems();
  });
  observer.observe(document.body, { childList: true, subtree: true });
});
</script>
